import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@foundation/components/ui/alert-dialog';
import { Button } from '@foundation/components/ui/button';
import { Input } from '@foundation/components/ui/input';
import { Textarea } from '@foundation/components/ui/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@foundation/components/ui/form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@foundation/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@foundation/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@foundation/components/ui/select';
import {
  ArrowLeft,
  Building,
  Calendar,
  ArrowRightLeft,
  ChevronRight,
  Clock,
  Copy,
  Crown,
  Edit,
  Loader2,
  Lock,
  LogOut,
  Mail,
  MoreVertical,
  Shield,
  Palette,
  Plus,
  Search,
  Settings,
  Trash2,
  User,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { BrandingEditor } from '../components/organization/BrandingEditor';
import { toast } from '@foundation/hooks/use-toast';
import {
  getUserOrganizations,
  getOrganizationMembers,
  updateOrganization,
  deleteOrganization,
  removeOrganizationMember,
  leaveOrganization,
  transferAdminAndLeave,
  updateMemberRole,
} from '@foundation/services/organizationService';
import { getOrganizationGroups } from '@foundation/services/teamService';
import {
  getSentInvites,
  cancelInvite,
  type SentInvite,
} from '@foundation/services/inviteService';
import { useOrganizationRoles } from '@foundation/hooks/useOrganizationRoles';
import { useUser } from '@foundation/contextapi/UserContext';
import type { Organization, OrganizationMember, MemberRole } from '@foundation/types/organization';
import type { Team } from '@foundation/services/teamService';
import { supabase } from '@foundation/utils/supabaseClient';

const editFormSchema = z.object({
  name: z.string().min(2, 'Organisation name must be at least 2 characters.'),
  description: z.string().optional(),
});
type EditFormValues = z.infer<typeof editFormSchema>;

type TeableSettings = { space_id: string | null; bases: Record<string, string> };

const AVATAR_PALETTE = ['#16a34a', '#0ea5e9', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899', '#0891b2'];
const pickColor = (name: string) => AVATAR_PALETTE[name.charCodeAt(0) % AVATAR_PALETTE.length];

export const OrganizationManagePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const { data: roles = [], isLoading: rolesLoading } = useOrganizationRoles(id || '');

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [sentInvites, setSentInvites] = useState<SentInvite[]>([]);
  const [teableSettings, setTeableSettings] = useState<TeableSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const initialTab = (['overview', 'members', 'branding'] as const).find(
    t => t === new URLSearchParams(window.location.search).get('tab')
  ) ?? 'overview';
  const initialBrandingTab = new URLSearchParams(window.location.search).get('brandingTab') as 'visual' | 'contact' | 'profile' | null;
  const [activeTab, setActiveTab] = useState<'overview' | 'members' | 'branding'>(initialTab);
  const [editMode, setEditMode] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<OrganizationMember | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [leavingOrg, setLeavingOrg] = useState(false);
  const [cancellingInvite, setCancellingInvite] = useState<string | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [pendingInvites, setPendingInvites] = useState<Array<{ email: string; roleId: string }>>([]);
  const [inviteErrors, setInviteErrors] = useState<Record<string, string>>({});
  const [sendingInvites, setSendingInvites] = useState(false);
  const [teableDialogOpen, setTeableDialogOpen] = useState(false);
  const [teableFormData, setTeableFormData] = useState({ space_id: '', base_name: '', base_id: '' });
  const [teableBases, setTeableBases] = useState<Array<{ name: string; id: string }>>([]);
  const [updatingTeable, setUpdatingTeable] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferTargetId, setTransferTargetId] = useState<string | null>(null);
  const [changingRoleMember, setChangingRoleMember] = useState<OrganizationMember | null>(null);
  const [defaultInviteRoleId, setDefaultInviteRoleId] = useState<string>('');
  // `null` until the first fetch resolves — prevents the disabled UI from
  // flashing on initial render before we know how many workspaces the user has.
  const [totalUserWorkspaces, setTotalUserWorkspaces] = useState<number | null>(null);

  const systemRoles = roles.filter(r => r.tenant_id === null);
  const customRoles = roles.filter(r => r.tenant_id !== null);

  const editForm = useForm<EditFormValues>({
    resolver: zodResolver(editFormSchema),
    defaultValues: { name: '', description: '' },
  });

  // ── Load org + members + teams + invites ──
  const loadData = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [orgData, membersData, teamsData, sentInvitesData] = await Promise.all([
        getUserOrganizations(),
        getOrganizationMembers(id),
        getOrganizationGroups(id),
        getSentInvites()
          .then(all => all.filter(inv => inv.tenant_id === id && inv.receiver_join_status === 'pending'))
          .catch((err) => {
            console.error('[OrganizationManagePage] Failed to load sent invites:', err);
            return [] as SentInvite[];
          }),
      ]);

      const found = orgData.organizations.find(o => o.id === id);
      if (!found) { navigate('/settings', { state: { tab: 'organizations' } }); return; }
      const membership = orgData.membership.find(m => m.organisation_id === id);
      // Prefer role from the RPC-based membersData (bypasses RLS, reliably returns role_code).
      // The getUserOrganizations join on base_roles(role_code) can silently return null when
      // RLS blocks SELECT on base_roles, causing org_admin to appear as 'member'.
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      const rpcMembership = membersData.find(m => m.user_id === currentUser?.id);
      const resolvedRole = (rpcMembership?.role as MemberRole) || (membership?.role as MemberRole) || 'member';
      const org: Organization = {
        ...found,
        role: resolvedRole,
      };
      setOrganization(org);
      setTotalUserWorkspaces(orgData.organizations.length);
      editForm.reset({ name: org.name, description: org.description || '' });
      setMembers(membersData);
      setTeams(teamsData);
      setSentInvites(sentInvitesData);
      // Fire-and-forget: don't block the rest of the page on Teable polling.
      // The Teable card has its own loading/empty states keyed off teableSettings.
      void loadTeableSettings(id, org);
    } catch (err) {
      console.error('OrganizationManagePage: failed to load', err);
      toast({ title: 'Error', description: 'Failed to load workspace data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Teable space + bases are written to base_tenants by an async onboarding
  // call (`triggerTeableOnboarding` in createOrganization) that is NOT awaited
  // before the user is navigated to this page. On first visit to a just-created
  // org, the row exists but space_id is still null. Without a retry the UI
  // shows "Teable space not linked" until the user refreshes manually.
  // We retry for freshly-created orgs to bridge that window; legacy orgs that
  // genuinely have no Teable link skip the retry and render immediately.
  const FRESH_ORG_WINDOW_MS = 60_000;
  const TEABLE_POLL_MAX_ATTEMPTS = 8;
  const TEABLE_POLL_INTERVAL_MS = 2_000;

  const loadTeableSettings = async (orgId: string, org?: Organization) => {
    const orgCreatedAt = org?.created_at ? new Date(org.created_at).getTime() : NaN;
    const isFreshOrg = Number.isFinite(orgCreatedAt)
      && Date.now() - orgCreatedAt < FRESH_ORG_WINDOW_MS;

    for (let attempt = 0; attempt < TEABLE_POLL_MAX_ATTEMPTS; attempt++) {
      const { data, error } = await supabase
        .from('base_tenants')
        .select('space_id, bases')
        .eq('tenant_id', orgId)
        .maybeSingle();

      if (error) {
        // Real error — log instead of silently swallowing, then fall through
        // to the "not linked" sentinel so the UI is still usable.
        console.error('[OrganizationManagePage] loadTeableSettings query failed:', error);
        setTeableSettings({ space_id: null, bases: {} });
        return;
      }

      const spaceId = data?.space_id ?? null;
      const bases = (data?.bases as Record<string, string> | undefined) || {};

      // Stop polling if we got a populated space_id, or if this is not a
      // freshly-created org (legacy orgs with no Teable link should render
      // their "not linked" state immediately without delay).
      if (spaceId || !isFreshOrg) {
        setTeableSettings({ space_id: spaceId, bases });
        return;
      }

      // Fresh org with space_id still null → onboarding likely still in flight.
      // Surface a transient "loading" state and retry after a delay.
      setTeableSettings(null);
      if (attempt < TEABLE_POLL_MAX_ATTEMPTS - 1) {
        await new Promise(resolve => setTimeout(resolve, TEABLE_POLL_INTERVAL_MS));
      }
    }

    // Exhausted retries — onboarding never wrote space_id within the window.
    // Fall back to the "not linked" sentinel so the user can manually link.
    console.warn('[OrganizationManagePage] Teable space_id never populated for fresh org', orgId);
    setTeableSettings({ space_id: null, bases: {} });
  };

  useEffect(() => { loadData(); }, [id]);

  const isPersonalWorkspace = organization?.tenant_type === 'personal';
  const inviteButtonText = isPersonalWorkspace ? 'Invite Users' : 'Invite Members';

  // Current viewer's role in this org
  const currentUserIsAdmin = organization?.role === 'admin' || organization?.role === 'org_admin';
  const adminCount = members.filter(m => m.role === 'admin' || m.role === 'org_admin').length;
  const hasOtherMembers = members.filter(m => m.user_id !== user?.id).length > 0;
  const nonAdminMembers = members.filter(m => m.role !== 'admin' && m.role !== 'org_admin' && m.user_id !== user?.id);
  // Invariant: a user must always be linked to at least one workspace.
  // When this is the only workspace the user has, leaving or deleting it is blocked.
  // Only treat as "only workspace" after the count has actually loaded — otherwise
  // every user briefly sees the disabled state during initial fetch.
  const isOnlyWorkspace = totalUserWorkspaces !== null && totalUserWorkspaces <= 1;
  const onlyWorkspaceMessage = 'This is your only workspace. Create or join another workspace before leaving or deleting it.';

  // ── Edit org ──
  const handleEdit = async (values: EditFormValues) => {
    if (!organization) return;
    try {
      await updateOrganization(organization.id, values.name, values.description);
      toast({ title: 'Success', description: 'Organisation updated successfully' });
      setEditMode(false);
      loadData();
    } catch {
      toast({ title: 'Error', description: 'Failed to update organisation', variant: 'destructive' });
    }
  };

  // ── Delete org ──
  const handleDelete = async () => {
    if (!organization) return;
    if (isOnlyWorkspace) {
      // Reachable only if the dialog was opened despite the disabled button —
      // log so the anomaly is traceable to a state change between open and confirm.
      console.warn('[OrganizationManagePage] Delete confirmed while isOnlyWorkspace=true; short-circuiting.');
      setDeleteDialogOpen(false);
      toast({
        title: 'Cannot Delete Organisation',
        description: onlyWorkspaceMessage,
        variant: 'destructive',
      });
      return;
    }
    try {
      await deleteOrganization(organization.id);
      toast({ title: 'Success', description: 'Organisation deleted successfully' });
      navigate('/settings', { state: { tab: 'organizations' } });
    } catch (error) {
      setDeleteDialogOpen(false);
      toast({
        title: 'Cannot Delete Organisation',
        description: error instanceof Error ? error.message : 'Failed to delete organisation',
        variant: 'destructive',
      });
    }
  };

  // ── Leave org ──
  const handleLeaveOrg = async () => {
    if (!organization) return;
    if (isOnlyWorkspace) {
      console.warn('[OrganizationManagePage] Leave confirmed while isOnlyWorkspace=true; short-circuiting.');
      setLeaveDialogOpen(false);
      toast({
        title: 'Cannot Leave Organisation',
        description: onlyWorkspaceMessage,
        variant: 'destructive',
      });
      return;
    }
    try {
      setLeavingOrg(true);
      await leaveOrganization(organization.id);
      toast({ title: 'Left organisation', description: `You have left ${organization.name}` });
      navigate('/settings', { state: { tab: 'organizations' } });
    } catch (error) {
      toast({
        title: 'Cannot Leave Organisation',
        description: error instanceof Error ? error.message : 'Failed to leave organisation',
        variant: 'destructive',
      });
    } finally {
      setLeavingOrg(false);
    }
  };

  // ── Transfer admin & leave ──
  const handleTransferAndLeave = async () => {
    if (!organization || !transferTargetId) return;
    if (isOnlyWorkspace) {
      console.warn('[OrganizationManagePage] Transfer-and-leave confirmed while isOnlyWorkspace=true; short-circuiting.');
      setTransferDialogOpen(false);
      setTransferTargetId(null);
      toast({
        title: 'Cannot Leave Organisation',
        description: onlyWorkspaceMessage,
        variant: 'destructive',
      });
      return;
    }
    try {
      setLeavingOrg(true);
      await transferAdminAndLeave(organization.id, transferTargetId);
      toast({ title: 'Admin transferred', description: `You have left ${organization.name}` });
      navigate('/settings', { state: { tab: 'organizations' } });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to transfer admin role',
        variant: 'destructive',
      });
    } finally {
      setLeavingOrg(false);
      setTransferTargetId(null);
      setTransferDialogOpen(false);
    }
  };

  // ── Change member role ──
  const handleChangeRole = async (member: OrganizationMember, newRole: 'admin' | 'member') => {
    if (!organization) return;
    try {
      await updateMemberRole(organization.id, member.user_id, newRole);
      toast({
        title: 'Role updated',
        description: `${member.full_name || member.email} is now ${newRole === 'admin' ? 'an Admin' : 'a Member'}`,
      });
      setChangingRoleMember(null);
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update role',
        variant: 'destructive',
      });
    }
  };

  // ── Remove member ──
  const handleRemoveMember = async (member: OrganizationMember) => {
    if (!organization) return;
    try {
      await removeOrganizationMember(organization.id, member.user_id);
      toast({ title: 'Success', description: 'Member removed successfully' });
      loadData();
    } catch {
      toast({ title: 'Error', description: 'Failed to remove member', variant: 'destructive' });
    }
  };

  // ── Teable settings ──
  const handleUpdateTeableSettings = async () => {
    if (!organization) return;
    const hasExistingSpaceId = teableSettings?.space_id;
    if (!hasExistingSpaceId && !teableFormData.space_id.trim()) {
      toast({ title: 'Error', description: 'Space ID is required', variant: 'destructive' });
      return;
    }
    try {
      setUpdatingTeable(true);
      const newBasesObject: Record<string, string> = {};
      teableBases.forEach(b => { if (b.name.trim() && b.id.trim()) newBasesObject[b.name.trim()] = b.id.trim(); });
      const requestBody: { organisationId: string; space_id?: string; bases?: Record<string, string> } = { organisationId: organization.id };
      if (!hasExistingSpaceId) requestBody.space_id = teableFormData.space_id.trim();
      if (Object.keys(newBasesObject).length > 0) requestBody.bases = newBasesObject;

      const { data: { session } } = await supabase.auth.getSession();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const res = await fetch(`${apiUrl}/api/altzero-spaces/teable/settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': session?.user?.id || '',
          'x-organisation-id': organization.id,
          ...(session?.access_token && { Authorization: `Bearer ${session.access_token}` }),
        },
        body: JSON.stringify(requestBody),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({ error: 'Failed' })); throw new Error(e.error); }
      const result = await res.json();
      toast({ title: 'Success', description: result.message || 'Teable settings saved' });
      setTeableDialogOpen(false);
      setTeableFormData({ space_id: '', base_name: '', base_id: '' });
      setTeableBases([]);
      await loadTeableSettings(organization.id);
    } catch (err) {
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed to update Teable settings', variant: 'destructive' });
    } finally {
      setUpdatingTeable(false);
    }
  };

  const handleAddBase = () => {
    if (!teableFormData.base_name.trim() || !teableFormData.base_id.trim()) {
      toast({ title: 'Error', description: 'Both base name and ID are required', variant: 'destructive' });
      return;
    }
    const existing = [...teableBases.map(b => b.name), ...(teableSettings?.bases ? Object.keys(teableSettings.bases) : [])];
    if (existing.includes(teableFormData.base_name.trim())) {
      toast({ title: 'Error', description: 'A base with this name already exists', variant: 'destructive' });
      return;
    }
    setTeableBases(prev => [...prev, { name: teableFormData.base_name.trim(), id: teableFormData.base_id.trim() }]);
    setTeableFormData(prev => ({ ...prev, base_name: '', base_id: '' }));
  };

  // ── Invite handlers ──
  const handleAddInvite = async () => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!inviteEmail.trim()) { setInviteErrors({ submit: 'Please enter an email address' }); return; }
    if (!emailPattern.test(inviteEmail)) { setInviteErrors({ submit: 'Please enter a valid email address' }); return; }
    if (pendingInvites.some(inv => inv.email === inviteEmail)) { setInviteErrors({ submit: 'This email is already in the list' }); return; }
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      const checkRes = await fetch(`${apiUrl}/api/invites/check-email/${encodeURIComponent(inviteEmail)}?tenantId=${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (checkRes.ok) {
        const { isMember } = await checkRes.json();
        if (isMember) { setInviteErrors({ submit: 'This user is already a member' }); return; }
      }
    } catch { /* fallthrough */ }
    const roleId = defaultInviteRoleId || (roles.length > 0 ? roles[0].role_id : '');
    setPendingInvites(prev => [...prev, { email: inviteEmail, roleId }]);
    setInviteEmail('');
    setInviteErrors({});
  };

  const handleSendInvites = async () => {
    if (pendingInvites.length === 0) { setInviteErrors({ submit: 'Please add at least one email' }); return; }
    setSendingInvites(true);
    setInviteErrors({});
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      for (const invite of pendingInvites) {
        let inviteType: 'personal' | 'organisation' = isPersonalWorkspace ? 'personal' : 'organisation';
        let tenantId: string | null = isPersonalWorkspace ? null : id || null;
        if (isPersonalWorkspace) {
          try {
            const checkRes = await fetch(`${apiUrl}/api/invites/check-email/${encodeURIComponent(invite.email)}?tenantId=${id}`, { headers: { Authorization: `Bearer ${token}` } });
            if (checkRes.ok) { const { exists } = await checkRes.json(); if (exists) { inviteType = 'organisation'; tenantId = id || null; } }
          } catch { /* fallthrough */ }
        }
        const res = await fetch(`${apiUrl}/api/invites/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ receiver_email: invite.email, invite_type: inviteType, tenant_id: tenantId, role_id: !isPersonalWorkspace ? invite.roleId || null : undefined }),
        });
        if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Failed to send invite'); }
      }
      toast({ title: 'Invitations Sent', description: `Sent to ${pendingInvites.length} recipient(s).` });
      setPendingInvites([]);
      setInviteEmail('');
      setInviteDialogOpen(false);
      loadData();
    } catch (err) {
      setInviteErrors({ submit: err instanceof Error ? err.message : 'Failed to send invitations' });
    } finally {
      setSendingInvites(false);
    }
  };

  const handleCancelInvite = async (inviteId: string) => {
    try {
      setCancellingInvite(inviteId);
      await cancelInvite(inviteId);
      toast({ title: 'Invite cancelled' });
      setSentInvites(prev => prev.filter(i => i.id !== inviteId));
    } catch {
      toast({ title: 'Error', description: 'Failed to cancel invite', variant: 'destructive' });
    } finally {
      setCancellingInvite(null);
    }
  };

  const filteredMembers = members.filter(m =>
    !memberSearch || (m.full_name || '').toLowerCase().includes(memberSearch.toLowerCase()) || (m.email || '').toLowerCase().includes(memberSearch.toLowerCase())
  );

  // ── Loading / not found ──
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!organization) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      {/* ── TOP HEADER ── */}
      <div className="bg-background border-b border-border px-4 sm:px-8 pt-4 sm:pt-6 pb-0">

        {/* Breadcrumb nav */}
        <div className="flex items-center gap-1.5 mb-4 text-xs text-muted-foreground">
          <button
            onClick={() => navigate('/settings', { state: { tab: 'organizations' } })}
            className="flex items-center gap-1 hover:text-foreground transition-colors"
          >
            <ArrowLeft size={12} />
            Settings
          </button>
          <ChevronRight size={12} />
          <span>Organisation</span>
          <ChevronRight size={12} />
          <span className="text-foreground font-medium">{organization.name}</span>
        </div>

        {/* Title row */}
        <div className="flex items-start justify-between gap-3 mb-4 sm:mb-5">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-semibold tracking-widest text-primary uppercase">
                {isPersonalWorkspace ? 'Personal Workspace' : 'Organisation Workspace'}
              </span>
            </div>
            <h1 className="text-2xl font-bold text-foreground tracking-tight">
              {organization.name}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {members.length} member{members.length !== 1 ? 's' : ''} · {teams.length} team{teams.length !== 1 ? 's' : ''}
            </p>
          </div>
          {currentUserIsAdmin && (
            <Button
              onClick={() => setInviteDialogOpen(true)}
              variant="outline"
              size="sm"
              className="flex items-center gap-2 mt-1 flex-shrink-0"
            >
              <UserPlus size={14} />
              {inviteButtonText}
            </Button>
          )}
        </div>

      </div>

      {/* ── SIDEBAR + CONTENT LAYOUT ── */}
      <div style={{ display: 'flex', height: 'calc(100vh - 140px)', overflow: 'hidden' }}>

        {/* Sidebar */}
        <div style={{ width: '220px', flexShrink: 0, borderRight: '1px solid hsl(var(--border))', background: 'hsl(var(--muted))', padding: '20px 12px', overflowY: 'auto' }}>
          {[
            { key: 'overview', label: 'Overview', icon: Building },
            { key: 'members', label: `Members`, badge: members.length, icon: Users },
            { key: 'branding', label: 'Branding', icon: Palette },
          ].map(({ key, label, icon: Icon, badge }: { key: string; label: string; icon: React.ElementType; badge?: number }) => {
            const active = activeTab === key;
            return (
              <button
                key={key}
                onClick={() => setActiveTab(key as 'overview' | 'members' | 'branding')}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '9px 12px', borderRadius: '9px', border: 'none', cursor: 'pointer',
                  marginBottom: '2px', textAlign: 'left', transition: 'background 0.15s',
                  background: active ? 'hsl(var(--accent))' : 'transparent',
                  color: active ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))',
                  fontWeight: active ? 600 : 400, fontSize: '13.5px',
                }}
                onMouseEnter={e => { if (!active) (e.currentTarget as HTMLElement).style.background = 'hsl(var(--muted))'; }}
                onMouseLeave={e => { if (!active) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
              >
                <span style={{ width: '28px', height: '28px', borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: active ? '#dcfce7' : '#f3f4f6', flexShrink: 0 }}>
                  <Icon size={14} color={active ? '#16a34a' : '#9ca3af'} />
                </span>
                <span style={{ flex: 1 }}>{label}</span>
                {badge !== undefined && (
                  <span style={{ background: active ? '#16a34a' : 'hsl(var(--border))', color: active ? '#fff' : '#6b7280', fontSize: '10px', fontWeight: 700, padding: '1px 6px', borderRadius: '9999px' }}>
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Content area */}
        <div style={{ flex: 1, padding: '28px 36px 80px', overflowY: 'auto' }}>

        {/* ── OVERVIEW TAB ── */}
        {activeTab === 'overview' && (
          <div className="flex justify-center">
            {editMode ? (
              <div style={{ background: 'hsl(var(--card))', borderRadius: '18px', border: '1px solid hsl(var(--border))', padding: 'clamp(16px, 5vw, 28px)', width: '100%', maxWidth: '560px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '22px' }}>
                  <button
                    onClick={() => setEditMode(false)}
                    style={{ width: '30px', height: '30px', borderRadius: '8px', background: 'hsl(var(--accent))', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'hsl(var(--muted-foreground))' }}
                  >
                    <X size={14} />
                  </button>
                  <h3 style={{ fontSize: '16px', fontWeight: 700, color: 'hsl(var(--foreground))', margin: 0 }}>Edit Details</h3>
                </div>
                <Form {...editForm}>
                  <form onSubmit={editForm.handleSubmit(handleEdit)} className="space-y-4">
                    <FormField control={editForm.control} name="name" render={({ field }) => (
                      <FormItem><FormLabel>Organisation Name</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={editForm.control} name="description" render={({ field }) => (
                      <FormItem><FormLabel>Description</FormLabel><FormControl><Textarea {...field} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <div className="flex gap-2 pt-2">
                      <Button type="submit">Save Changes</Button>
                      <Button type="button" variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
                    </div>
                  </form>
                </Form>
              </div>
            ) : (
              <div className="flex justify-center">

                {/* ── Organisation Details Card ── */}
                <div style={{ background: 'hsl(var(--card))', borderRadius: '18px', border: '1px solid hsl(var(--border))', padding: '26px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', width: '100%', maxWidth: '560px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '22px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: 'hsl(var(--accent))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Building size={17} color="#16a34a" />
                      </div>
                      <span style={{ fontSize: '13.5px', fontWeight: 700, color: 'hsl(var(--foreground))' }}>
                        {isPersonalWorkspace ? 'Workspace Details' : 'Organisation Details'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {currentUserIsAdmin && (
                        <button
                          onClick={() => setEditMode(true)}
                          style={{ width: '30px', height: '30px', borderRadius: '7px', background: 'hsl(var(--accent))', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'hsl(var(--muted-foreground))' }}
                          title="Edit"
                        >
                          <Edit size={13} />
                        </button>
                      )}
                      {currentUserIsAdmin && !hasOtherMembers && (
                        <button
                          onClick={() => setDeleteDialogOpen(true)}
                          disabled={isOnlyWorkspace}
                          aria-label={isOnlyWorkspace ? onlyWorkspaceMessage : 'Delete Organisation'}
                          title={isOnlyWorkspace ? onlyWorkspaceMessage : 'Delete Organisation'}
                          style={{ width: '30px', height: '30px', borderRadius: '7px', background: isOnlyWorkspace ? '#f3f4f6' : '#fff0f0', border: 'none', cursor: isOnlyWorkspace ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isOnlyWorkspace ? '#9ca3af' : '#ef4444', opacity: isOnlyWorkspace ? 0.6 : 1 }}
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                      <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', marginBottom: '4px' }}>NAME</div>
                      <div style={{ fontSize: '15px', fontWeight: 700, color: 'hsl(var(--foreground))' }}>{organization.name}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', marginBottom: '4px' }}>CREATED</div>
                      <div style={{ fontSize: '13.5px', color: 'hsl(var(--muted-foreground))', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Calendar size={12} color="hsl(var(--muted-foreground))" />
                        {new Date(organization.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', marginBottom: '4px' }}>DESCRIPTION</div>
                      <div style={{ fontSize: '13.5px', color: 'hsl(var(--muted-foreground))', lineHeight: 1.6 }}>
                        {organization.description || 'No description provided'}
                      </div>
                    </div>

                    {/* Stats */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', paddingTop: '12px', borderTop: '1px solid hsl(var(--border))' }}>
                      <div style={{ background: 'hsl(var(--accent))', borderRadius: '12px', padding: '14px', textAlign: 'center' }}>
                        <div style={{ fontSize: '26px', fontWeight: 800, color: '#16a34a', lineHeight: 1 }}>{members.length}</div>
                        <div style={{ fontSize: '11px', color: 'hsl(var(--muted-foreground))', fontWeight: 600, marginTop: '4px' }}>Members</div>
                      </div>
                      <div style={{ background: 'hsl(var(--accent))', borderRadius: '12px', padding: '14px', textAlign: 'center' }}>
                        <div style={{ fontSize: '26px', fontWeight: 800, color: '#16a34a', lineHeight: 1 }}>{teams.length}</div>
                        <div style={{ fontSize: '11px', color: 'hsl(var(--muted-foreground))', fontWeight: 600, marginTop: '4px' }}>Teams</div>
                      </div>
                    </div>

                    {/* Leave button — any member can leave if they have another workspace */}
                    {!isPersonalWorkspace && (
                      currentUserIsAdmin ? (
                        adminCount >= 2 ? (
                          // Multi-admin: can leave directly
                          <button
                            onClick={() => setLeaveDialogOpen(true)}
                            disabled={isOnlyWorkspace}
                            aria-label={isOnlyWorkspace ? onlyWorkspaceMessage : 'Leave Organisation'}
                            title={isOnlyWorkspace ? onlyWorkspaceMessage : 'Leave Organisation'}
                            style={{ background: 'none', border: `1px solid ${isOnlyWorkspace ? 'hsl(var(--border))' : '#fca5a5'}`, borderRadius: '10px', padding: '10px 16px', fontSize: '13px', fontWeight: 600, color: isOnlyWorkspace ? '#9ca3af' : '#ef4444', cursor: isOnlyWorkspace ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', opacity: isOnlyWorkspace ? 0.6 : 1 }}
                          >
                            <LogOut size={14} />
                            Leave Organisation
                          </button>
                        ) : hasOtherMembers ? (
                          // Solo admin with other members: must transfer role first
                          <button
                            onClick={() => setTransferDialogOpen(true)}
                            disabled={isOnlyWorkspace}
                            aria-label={isOnlyWorkspace ? onlyWorkspaceMessage : 'Transfer Admin & Leave'}
                            title={isOnlyWorkspace ? onlyWorkspaceMessage : 'Transfer Admin & Leave'}
                            style={{ background: 'none', border: `1px solid ${isOnlyWorkspace ? 'hsl(var(--border))' : '#fca5a5'}`, borderRadius: '10px', padding: '10px 16px', fontSize: '13px', fontWeight: 600, color: isOnlyWorkspace ? '#9ca3af' : '#ef4444', cursor: isOnlyWorkspace ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', opacity: isOnlyWorkspace ? 0.6 : 1 }}
                          >
                            <ArrowRightLeft size={14} />
                            Transfer Admin & Leave
                          </button>
                        ) : null // Solo admin, no other members — use delete instead
                      ) : (
                        // Non-admin member: can always leave (unless this is their only workspace)
                        <button
                          onClick={() => setLeaveDialogOpen(true)}
                          disabled={isOnlyWorkspace}
                          aria-label={isOnlyWorkspace ? onlyWorkspaceMessage : 'Leave Organisation'}
                          title={isOnlyWorkspace ? onlyWorkspaceMessage : 'Leave Organisation'}
                          style={{ background: 'none', border: `1px solid ${isOnlyWorkspace ? 'hsl(var(--border))' : '#fca5a5'}`, borderRadius: '10px', padding: '10px 16px', fontSize: '13px', fontWeight: 600, color: isOnlyWorkspace ? '#9ca3af' : '#ef4444', cursor: isOnlyWorkspace ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', opacity: isOnlyWorkspace ? 0.6 : 1 }}
                        >
                          <LogOut size={14} />
                          Leave Organisation
                        </button>
                      )
                    )}
                    {!isPersonalWorkspace && isOnlyWorkspace && (
                      // Inline note so the disabled state is discoverable on touch devices and screen readers
                      <p style={{ fontSize: '11.5px', color: 'hsl(var(--muted-foreground))', margin: '6px 0 0 0', lineHeight: 1.45 }}>
                        {onlyWorkspaceMessage}
                      </p>
                    )}
                  </div>
                </div>

              </div>
            )}
          </div>
        )}

        {/* ── MEMBERS TAB ── */}
        {activeTab === 'members' && (
          <div>
            {/* Search + invite */}
            <div className="flex items-center justify-between gap-3 mb-[18px] flex-wrap">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'hsl(var(--card))', borderRadius: '9999px', border: '1px solid hsl(var(--border))', padding: '9px 16px 9px 12px', flex: 1, minWidth: 0 }}>
                <Search size={14} color="hsl(var(--muted-foreground))" />
                <input
                  type="text"
                  placeholder="Search members…"
                  value={memberSearch}
                  onChange={e => setMemberSearch(e.target.value)}
                  style={{ background: 'none', border: 'none', outline: 'none', fontSize: '13px', color: 'hsl(var(--muted-foreground))', flex: 1 }}
                />
              </div>
              {currentUserIsAdmin && (
                <button
                  onClick={() => setInviteDialogOpen(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13.5px', color: '#16a34a', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, padding: '9px 4px' }}
                >
                  Invite Member <ChevronRight size={13} />
                </button>
              )}
            </div>

            {/* Members table */}
            <div className="overflow-x-auto rounded-[18px] border mb-5" style={{ borderColor: 'hsl(var(--border))' }}>
            <div style={{ background: 'hsl(var(--card))', borderRadius: '18px', minWidth: '520px', overflow: 'hidden' }}>
              {/* Table header */}
              <div style={{ display: 'grid', gridTemplateColumns: '2.5fr 1fr 1.2fr 100px 40px', padding: '12px 20px', background: 'hsl(var(--accent))', borderBottom: '1px solid hsl(var(--border))' }}>
                {['MEMBER', 'ROLE', 'JOINED DATE', 'STATUS', ''].map((col, i) => (
                  <div key={i} style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', padding: '0 8px' }}>{col}</div>
                ))}
              </div>

              {filteredMembers.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: 'hsl(var(--muted-foreground))', fontSize: '14px' }}>
                  {memberSearch ? 'No members match your search' : 'No members yet'}
                </div>
              ) : (
                filteredMembers.map((member, idx) => {
                  const isAdmin = member.role === 'admin' || member.role === 'org_admin';
                  const nameOrEmail = member.full_name || member.email || 'Unknown';
                  const initial = nameOrEmail.charAt(0).toUpperCase();
                  const avatarBg = pickColor(nameOrEmail);
                  const memberWithDate = member as OrganizationMember & { created_at?: string };

                  return (
                    <div
                      key={member.id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '2.5fr 1fr 1.2fr 100px 40px',
                        padding: '13px 20px',
                        borderBottom: idx < filteredMembers.length - 1 ? '1px solid hsl(var(--border))' : 'none',
                        alignItems: 'center',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={e => ((e.currentTarget as HTMLDivElement).style.background = '#fafffe')}
                      onMouseLeave={e => ((e.currentTarget as HTMLDivElement).style.background = 'transparent')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '0 8px' }}>
                        <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: avatarBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <span style={{ color: '#fff', fontSize: '13px', fontWeight: 700 }}>{initial}</span>
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'hsl(var(--foreground))', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {member.full_name || 'Unknown'}
                          </div>
                          <div style={{ fontSize: '11.5px', color: 'hsl(var(--muted-foreground))', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{member.email}</div>
                        </div>
                      </div>
                      <div style={{ padding: '0 8px' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                          fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.06em',
                          padding: '3px 9px', borderRadius: '9999px',
                          background: isAdmin ? '#0d1f14' : 'hsl(var(--accent))',
                          color: isAdmin ? '#4ade80' : 'hsl(var(--muted-foreground))',
                        }}>
                          {isAdmin ? <Crown size={9} /> : <User size={9} />}
                          {isAdmin ? 'OWNER' : (member.role ? member.role.toUpperCase() : 'MEMBER')}
                        </span>
                      </div>
                      <div style={{ padding: '0 8px', fontSize: '12.5px', color: 'hsl(var(--muted-foreground))' }}>
                        {memberWithDate.created_at
                          ? new Date(memberWithDate.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : '—'}
                      </div>
                      <div style={{ padding: '0 8px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, color: '#16a34a' }}>
                          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#16a34a', flexShrink: 0, boxShadow: '0 0 0 2px #dcfce7' }} />
                          Active
                        </span>
                      </div>
                      <div style={{ padding: '0 4px' }}>
                        {/* Actions only for current admin, on non-admin members, not own row */}
                        {currentUserIsAdmin && member.user_id !== user?.id && !isAdmin && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button style={{ width: '28px', height: '28px', borderRadius: '7px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'hsl(var(--muted-foreground))' }}>
                                <MoreVertical size={14} />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              {/* Promote to admin */}
                              <DropdownMenuItem onClick={() => handleChangeRole(member, 'admin')}>
                                <Crown className="w-4 h-4 mr-2" />
                                Make Admin
                              </DropdownMenuItem>
                              {/* Remove */}
                              <DropdownMenuItem onClick={() => setMemberToRemove(member)} className="text-destructive">
                                <Trash2 className="w-4 h-4 mr-2" />
                                Remove Member
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            </div>

            {/* Pending Invitations */}
            {!isPersonalWorkspace && (
              <div style={{ background: 'hsl(var(--card))', borderRadius: '18px', border: '1px solid hsl(var(--border))', overflow: 'hidden' }}>
                <div style={{ padding: '14px 20px', background: 'hsl(var(--accent))', borderBottom: sentInvites.length > 0 ? '1px solid hsl(var(--border))' : 'none', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Clock size={14} color="hsl(var(--muted-foreground))" />
                  <span style={{ fontSize: '11px', fontWeight: 700, color: 'hsl(var(--muted-foreground))', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    Pending Invitations {sentInvites.length > 0 && `(${sentInvites.length})`}
                  </span>
                </div>
                {sentInvites.length === 0 ? (
                  <div style={{ padding: '24px 20px', textAlign: 'center', fontSize: '13.5px', color: 'hsl(var(--muted-foreground))' }}>No pending invitations</div>
                ) : (
                  sentInvites.map((invite, idx) => (
                    <div key={invite.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 20px', borderBottom: idx < sentInvites.length - 1 ? '1px solid hsl(var(--border))' : 'none' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
                        <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: 'hsl(var(--accent))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Mail size={14} color="hsl(var(--muted-foreground))" />
                        </div>
                        <div>
                          <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'hsl(var(--muted-foreground))' }}>{invite.receiver_email}</div>
                          <div style={{ fontSize: '12px', color: 'hsl(var(--muted-foreground))' }}>
                            {invite.role_name ? `${invite.role_name} · ` : ''}
                            Invited {new Date(invite.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '9.5px', fontWeight: 700, background: '#fef3c7', color: '#92400e', padding: '3px 9px', borderRadius: '9999px', letterSpacing: '0.05em' }}>PENDING</span>
                        <button
                          onClick={() => handleCancelInvite(invite.id)}
                          disabled={cancellingInvite === invite.id}
                          style={{ width: '28px', height: '28px', borderRadius: '7px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'hsl(var(--muted-foreground))' }}
                        >
                          {cancellingInvite === invite.id ? <Loader2 size={13} className="animate-spin" /> : <X size={13} />}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}
        {/* ── BRANDING TAB ── */}
        {activeTab === 'branding' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
              <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: 'hsl(var(--accent))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Palette size={17} color="#16a34a" />
              </div>
              <div>
                <h2 style={{ fontSize: '16px', fontWeight: 700, color: 'hsl(var(--foreground))', margin: 0 }}>Branding</h2>
                <p style={{ fontSize: '12.5px', color: 'hsl(var(--muted-foreground))', margin: 0 }}>
                  Customize logos, colours and fonts for this workspace
                </p>
              </div>
            </div>
            {!currentUserIsAdmin && (
              <div className='flex items-center gap-3 mb-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-400'>
                <Lock className='h-4 w-4 shrink-0' />
                <p className='text-sm'>
                  Branding settings can only be changed by organisation admins.
                  Contact your admin to update these details.
                </p>
              </div>
            )}
            <div
              style={{ background: 'hsl(var(--card))', borderRadius: '18px', border: '1px solid hsl(var(--border))', padding: 'clamp(16px, 5vw, 28px)', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
            >
              <BrandingEditor
                organization={organization}
                initialTab={initialBrandingTab ?? undefined}
                onSuccess={() =>
                  toast({ title: 'Success', description: 'Branding updated successfully' })
                }
              />
            </div>
          </div>
        )}

        </div>{/* end content area */}
      </div>{/* end sidebar + content wrapper */}

      {/* ── Green FAB — admin only ── */}
      {currentUserIsAdmin && (
        <button
          onClick={() => setInviteDialogOpen(true)}
          style={{
            position: 'fixed', bottom: '32px', right: '36px',
            width: '52px', height: '52px', borderRadius: '50%',
            background: '#16a34a', color: '#fff', border: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', boxShadow: '0 4px 24px rgba(22,163,74,0.5)',
            zIndex: 50, transition: 'transform 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.1)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 28px rgba(22,163,74,0.6)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 24px rgba(22,163,74,0.5)'; }}
          title="Invite Members"
        >
          <Plus size={22} />
        </button>
      )}

      {/* ── ALERT DIALOGS ── */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Organisation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{organization.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave Organisation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to leave "{organization.name}"? You will need to be re-invited to rejoin.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={leavingOrg}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleLeaveOrg} disabled={leavingOrg} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {leavingOrg ? 'Leaving…' : 'Leave Organisation'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Transfer Admin & Leave */}
      <AlertDialog open={transferDialogOpen} onOpenChange={v => { setTransferDialogOpen(v); if (!v) setTransferTargetId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-orange-500" />
              Transfer Admin & Leave
            </AlertDialogTitle>
            <AlertDialogDescription>
              Select a member to transfer the admin role to. You will leave "{organization.name}" after the transfer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Select onValueChange={setTransferTargetId} value={transferTargetId || undefined}>
              <SelectTrigger>
                <SelectValue placeholder="Select new admin" />
              </SelectTrigger>
              <SelectContent>
                {nonAdminMembers.map(m => (
                  <SelectItem key={m.user_id} value={m.user_id}>
                    {m.full_name || m.email} {m.full_name ? `(${m.email})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={leavingOrg}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleTransferAndLeave}
              disabled={!transferTargetId || leavingOrg}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {leavingOrg ? 'Transferring…' : 'Transfer & Leave'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove Member */}
      <AlertDialog open={!!memberToRemove} onOpenChange={() => setMemberToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Member</AlertDialogTitle>
            <AlertDialogDescription>Remove {memberToRemove?.full_name} from this organisation?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (memberToRemove) { handleRemoveMember(memberToRemove); setMemberToRemove(null); } }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── INVITE DIALOG ── */}
      <Dialog open={inviteDialogOpen} onOpenChange={v => { setInviteDialogOpen(v); if (!v) { setPendingInvites([]); setInviteEmail(''); setInviteErrors({}); setDefaultInviteRoleId(''); } }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              {isPersonalWorkspace ? `Invite Users to ${organization.name}` : `Invite Members to ${organization.name}`}
            </DialogTitle>
            <p className="text-sm text-muted-foreground">Enter email addresses and assign a role to send invitations</p>
          </DialogHeader>

          <div className="space-y-4">
            {/* Upfront role selector */}
            <div className="space-y-1.5">
              <label className="text-sm font-semibold">Assign Role</label>
              <Select
                value={defaultInviteRoleId}
                onValueChange={val => {
                  setDefaultInviteRoleId(val);
                  // Update all already-added pending invites with the new role
                  setPendingInvites(prev => prev.map(i => ({ ...i, roleId: val })));
                }}
                disabled={sendingInvites || rolesLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder={rolesLoading ? 'Loading roles…' : 'Select a role'} />
                </SelectTrigger>
                <SelectContent>
                  {rolesLoading ? (
                    <SelectItem value="_loading" disabled>Loading…</SelectItem>
                  ) : (
                    <>
                      {systemRoles.length > 0 && (
                        <SelectGroup>
                          <SelectLabel className="flex items-center gap-1">
                            <Shield className="h-3 w-3" /> System
                          </SelectLabel>
                          {systemRoles.map(r => (
                            <SelectItem key={r.role_id} value={r.role_id}>{r.role_name}</SelectItem>
                          ))}
                        </SelectGroup>
                      )}
                      {customRoles.length > 0 && (
                        <SelectGroup>
                          <SelectLabel className="flex items-center gap-1">
                            <Users className="h-3 w-3" /> Custom
                          </SelectLabel>
                          {customRoles.map(r => (
                            <SelectItem key={r.role_id} value={r.role_id}>{r.role_name}</SelectItem>
                          ))}
                        </SelectGroup>
                      )}
                    </>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">This role will be assigned to all invited users.</p>
            </div>

            {/* Email input */}
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="Enter email address"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddInvite(); } }}
                disabled={sendingInvites}
                className="flex-1"
              />
              <Button type="button" variant="outline" onClick={handleAddInvite} disabled={sendingInvites}>
                <Plus className="w-4 h-4 mr-1" />Add
              </Button>
            </div>

            {/* Pending invite list */}
            {pendingInvites.length > 0 && (
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {pendingInvites.map(invite => (
                  <div key={invite.email} className="flex items-center gap-2 p-2 bg-muted rounded-md">
                    <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="text-sm truncate min-w-0 flex-1">{invite.email}</span>
                    <Button
                      type="button" variant="ghost" size="sm"
                      className="shrink-0 h-8 w-8 p-0"
                      onClick={() => setPendingInvites(prev => prev.filter(i => i.email !== invite.email))}
                      disabled={sendingInvites}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {inviteErrors.submit && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">{inviteErrors.submit}</p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => { setInviteDialogOpen(false); setPendingInvites([]); setInviteEmail(''); setInviteErrors({}); setDefaultInviteRoleId(''); }}
              disabled={sendingInvites}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendInvites}
              disabled={pendingInvites.length === 0 || sendingInvites}
              className="bg-[#16a34a] hover:bg-[#15803d]"
            >
              <Mail className="w-4 h-4 mr-2" />
              {sendingInvites ? 'Sending…' : 'Send Invites'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── TEABLE SETTINGS DIALOG ── */}
      <Dialog open={teableDialogOpen} onOpenChange={setTeableDialogOpen}>
        <DialogContent className="sm:max-w-[580px]">
          <DialogHeader>
            <DialogTitle>{teableSettings?.space_id ? 'Manage Teable Bases' : 'Link Teable Space & Bases'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Space ID {!teableSettings?.space_id && <span className="text-destructive">*</span>}</label>
              <Input placeholder="Teable space ID" value={teableFormData.space_id} onChange={e => setTeableFormData(p => ({ ...p, space_id: e.target.value }))}
                disabled={updatingTeable || !!teableSettings?.space_id} readOnly={!!teableSettings?.space_id}
                className={teableSettings?.space_id ? 'bg-muted cursor-not-allowed' : ''} />
              {teableSettings?.space_id && <p className="text-xs text-muted-foreground">Space ID is view-only.</p>}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Add Bases</label>
              {teableSettings?.bases && Object.keys(teableSettings.bases).length > 0 && (
                <div className="space-y-1 mb-2">
                  <p className="text-xs text-muted-foreground">Existing bases (read-only):</p>
                  {Object.entries(teableSettings.bases).map(([name, id]) => (
                    <div key={name} className="flex items-center gap-2 p-2 bg-muted rounded-md text-sm">
                      <strong>{name}:</strong><code className="ml-1 text-xs bg-background px-1 py-0.5 rounded">{id}</code>
                    </div>
                  ))}
                </div>
              )}
              {teableBases.length > 0 && (
                <div className="space-y-1 mb-2">
                  {teableBases.map((base, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-primary/10 border border-primary/20 rounded-md text-sm">
                      <strong>{base.name}:</strong><code className="ml-1 text-xs bg-background px-1 py-0.5 rounded">{base.id}</code>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setTeableBases(p => p.filter((_, j) => j !== i))} disabled={updatingTeable}><X className="w-4 h-4" /></Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input placeholder="Base name" value={teableFormData.base_name} onChange={e => setTeableFormData(p => ({ ...p, base_name: e.target.value }))} disabled={updatingTeable} />
                <Input placeholder="Base ID" value={teableFormData.base_id} onChange={e => setTeableFormData(p => ({ ...p, base_id: e.target.value }))} disabled={updatingTeable} />
                <Button type="button" variant="outline" onClick={handleAddBase} disabled={updatingTeable}><Plus className="w-4 h-4 mr-1" />Add</Button>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => { setTeableDialogOpen(false); setTeableFormData({ space_id: '', base_name: '', base_id: '' }); setTeableBases([]); }} disabled={updatingTeable}>Cancel</Button>
            <Button onClick={handleUpdateTeableSettings} disabled={updatingTeable || (!teableSettings?.space_id && !teableFormData.space_id.trim())}>
              {updatingTeable ? 'Saving…' : 'Save Settings'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
